/**
 * Withings API Service
 * Handles communication with Withings API for scale data
 */

import type {
  WithingsMeasurementsResponse,
  WithingsDeviceResponse,
  WithingsApiMeasurement,
  WithingsApiDevice,
  WithingsGetMeasurementsRequest,
  WithingsGetDeviceRequest,
} from "../types/api";
import type {
  WithingsScaleData,
  WithingsUser,
  WithingsDevice,
  WithingsMeasurement,
} from "../types/domain";

import { fetch } from "@tauri-apps/plugin-http";

import { debugHealth } from "@/domains/logging/utils/debugLog";
import { getSupabaseClient } from "@/shared/services";

import { WITHINGS_MEASUREMENT_TYPES } from "../types/api";
import { WithingsTokenService } from "./withings-token-service";

/**
 * Type alias for Withings API request parameters
 */
type WithingsRequestParams = Record<string, number | string | undefined>;

/**
 * Withings API Service
 * Manages API calls to Withings and data synchronization
 */
const API_BASE_URL = "https://wbsapi.withings.net";

/**
 * Initialize Withings service with default configuration
 *
 * @returns {Promise<void>}
 *
 */
export async function initialize(): Promise<void> {
  try {
    // Check if config already exists
    const existingConfig = await WithingsTokenService.getConfig();
    if (existingConfig) {
      return;
    }

    // Initialize with default config (will be overridden by user settings)
    const defaultConfig = {
      authUrl: "https://account.withings.com/oauth2_user/authorize2",
      clientId: "", // Will be set from settings
      clientSecret: "", // Will be set from settings
      redirectUri: "tracq://oauth/withings/callback", // Match the UI default
      tokenUrl: "https://wbsapi.withings.net/v2/oauth2",
    };

    await WithingsTokenService.initializeConfig(defaultConfig);
  } catch (error) {
    // NOTE: Don't throw error, just log it - user can configure later
    // Reason: Initialization failures should not block the application
    if (error instanceof Error) {
      // Silent error handling for initialization
    }
  }
}

/**
 * Get all scale measurements from Withings API
 * Fetches ALL available measurement types from your scale
 *
 * @param startDate
 *
 * @param endDate
 *
 * @returns {Promise<WithingsScaleData[]>} Array of scale measurements
 *
 */
export async function getMeasurements(
  startDate?: Date,
  endDate?: Date,
): Promise<WithingsScaleData[]> {
  try {
    const accessToken = await WithingsTokenService.getValidAccessToken();
    const tokens = await WithingsTokenService.getStoredTokens();

    if (!tokens) {
      throw new Error("No Withings tokens found");
    }

    // Always fetch ALL available measurement types from your scale
    // Query only the 9 key measurements (excluding heart rate)
    const allMeasurementTypes = "1,5,6,8,76,77,88,170,226"; // weight, fat_free_mass, body_fat, fat_mass, muscle_mass, body_water, bone_mass, visceral_fat, bmr

    const request: WithingsGetMeasurementsRequest = {
      action: "getmeas",
      category: 1, // Real measures
      meastypes: allMeasurementTypes, // All available measurement types
    };

    // Only add date parameters if they are provided
    if (startDate) {
      request.startdate = Math.floor(startDate.getTime() / 1000);
    }
    if (endDate) {
      request.enddate = Math.floor(endDate.getTime() / 1000);
    }

    const response = await makeApiCall<WithingsMeasurementsResponse>(
      "/measure",
      request as WithingsRequestParams,
      accessToken,
    );

    const measurements = transformMeasurementsResponse(response, tokens.userId);

    return measurements;
  } catch (error) {
    // NOTE: Re-throw with context for better error handling
    throw new Error(
      `Failed to get Withings measurements: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Get your user profile from Withings API
 * Note: The /user endpoint is not implemented by Withings API
 * We'll use the device endpoint instead to get basic user info
 *
 * @returns {Promise<null | WithingsUser>} User profile or null
 *
 */
export async function getUserProfile(): Promise<null | WithingsUser> {
  try {
    const accessToken = await WithingsTokenService.getValidAccessToken();
    const tokens = await WithingsTokenService.getStoredTokens();

    if (!tokens) {
      throw new Error("No Withings tokens found");
    }

    // Use device endpoint instead of user endpoint (which is not implemented)
    const request: WithingsGetDeviceRequest = {
      action: "get",
      userid: tokens.userId,
    };

    const response = await makeApiCall<WithingsDeviceResponse>(
      "/device",
      request as WithingsRequestParams,
      accessToken,
    );

    if (response.body.devices.length > 0) {
      // Create a minimal user profile from device info
      const userProfile: WithingsUser = {
        // deviceId: response.body.devices[0]?.id ?? "unknown", // NOTE: deviceId not in WithingsUser interface
        email: `user-${tokens.userId}@withings.com`,
        firstName: "Withings",
        id: tokens.userId,
        lastName: "User",
        shortName: "WU",
      };

      return userProfile;
    }

    return null;
  } catch (error) {
    // NOTE: Don't throw error, just return null
    // Reason: User profile fetch failures should not disrupt the application
    if (error instanceof Error) {
      // Silent error handling for user profile fetch
    }
    return null;
  }
}

/**
 * Get your devices from Withings API
 *
 * @returns {Promise<WithingsDevice[]>} Array of Withings devices
 *
 */
export async function getDevices(): Promise<WithingsDevice[]> {
  try {
    const accessToken = await WithingsTokenService.getValidAccessToken();
    const tokens = await WithingsTokenService.getStoredTokens();

    if (!tokens) {
      throw new Error("No Withings tokens found");
    }

    const request: WithingsGetDeviceRequest = {
      action: "get",
      userid: tokens.userId,
    };

    const response = await makeApiCall<WithingsDeviceResponse>(
      "/device",
      request as WithingsRequestParams,
      accessToken,
    );

    const devices = response.body.devices.map((device) => transformDeviceResponse(device));
    return devices;
  } catch (error) {
    // NOTE: Don't throw error, just return empty array
    // Reason: Device fetch failures should not disrupt the application
    if (error instanceof Error) {
      // Silent error handling for device fetch
    }
    return [];
  }
}

/**
 * Sync measurements to Supabase database
 *
 * @returns {Promise<void>}
 *
 */
export async function syncMeasurementsToDatabase(): Promise<void> {
  const measurements = await getMeasurements();

  // NOTE: Tokens retrieved but not used in this function
  await WithingsTokenService.getStoredTokens();

  // NOTE: Measurements retrieved but not used in this function
  if (measurements.length > 0) {
    // NOTE: First measurement available but not used
  }

  // Skip Supabase sync if not authenticated (but still show validation results)
  try {
    const supabase = await getSupabaseClient();
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) {
      return; // Don't throw error, just skip database sync
    }

    if (!user) {
      return; // Don't throw error, just skip database sync
    }

    // Batch insert measurements
    const measurementData = measurements.map((measurement) => ({
      bmi: measurement.bmi,
      body_fat: measurement.bodyFat,
      body_water: measurement.bodyWater,
      bone_mass: measurement.boneMass,
      device_id: measurement.deviceId,
      heart_rate: measurement.heartRate,
      measurement_id: measurement.id,
      muscle_mass: measurement.muscleMass,
      raw_data: measurement,
      timestamp: measurement.timestamp.toISOString(),
      user_id: user.id,
      visceral_fat: measurement.visceralFat,
      weight: measurement.weight,
      withings_user_id: measurement.userId,
    }));

    const { error } = await supabase.from("withings_measurements").upsert(measurementData, {
      ignoreDuplicates: false,
      onConflict: "measurement_id",
    });

    if (error) {
      throw error;
    }
  } catch {
    // NOTE: Don't throw error, just log it - validation was successful
  }
}

/**
 * Sync your user profile to Supabase database (optional)
 *
 * @returns {Promise<void>}
 *
 */
export async function syncUserProfileToDatabase(): Promise<void> {
  try {
    const userProfile = await getUserProfile();
    if (!userProfile) {
      return;
    }

    // Skip Supabase sync if not authenticated (but still show profile info)
    try {
      const supabase = await getSupabaseClient();
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) {
        return;
      }

      if (!user) {
        return;
      }

      const profileData = {
        birthdate: userProfile.birthdate?.toISOString().split("T")[0],
        bone_mass: userProfile.boneMass,
        email: userProfile.email,
        fat_free_mass: userProfile.fatFreeMass,
        fat_mass: userProfile.fatMass,
        first_name: userProfile.firstName,
        gender: userProfile.gender,
        height: userProfile.height,
        last_name: userProfile.lastName,
        muscle_mass: userProfile.muscleMass,
        short_name: userProfile.shortName,
        user_id: user.id,
        weight: userProfile.weight,
        withings_user_id: userProfile.id,
      };

      const { error } = await supabase.from("withings_user_profiles").upsert(profileData, {
        ignoreDuplicates: false,
        onConflict: "withings_user_id",
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      // NOTE: Error during database sync - log but don't throw to prevent UI disruption
      const message = error instanceof Error ? error.message : String(error);
      debugHealth("error", "withings", "[WithingsService] Database sync error", {
        error: message,
        file: "withingsService.ts",
        function: "syncUserProfileToDatabase",
      });
    }
  } catch (error) {
    // NOTE: Error during measurement sync - log but don't throw
    debugHealth("error", "withings", "[WithingsService] Measurement sync error", {
      error: error instanceof Error ? error.message : String(error),
      file: "withingsService.ts",
      function: "syncUserProfileToDatabase",
    });
  }
}

/**
 * Get measurements from Supabase database
 *
 * @param limit
 *
 * @param offset
 *
 * @returns {Promise<WithingsScaleData[]>} Array of scale measurements
 *
 */
export async function getMeasurementsFromDatabase(
  limit = 100,
  offset = 0,
): Promise<WithingsScaleData[]> {
  try {
    const supabase = await getSupabaseClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
      .from("withings_measurements_view")
      .select("*")
      .order("measurement_date", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const safeData = Array.isArray(data) ? data : [];
    return safeData.map((databaseMeasurement) =>
      transformDatabaseMeasurement(databaseMeasurement as WithingsMeasurement),
    );
  } catch (error) {
    // NOTE: Re-throw with context for better error handling
    throw new Error(
      `Failed to get measurements from database: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Make authenticated API call to Withings
 *
 * @param endpoint
 *
 * @param parameters
 *
 * @param accessToken
 *
 * @returns {Promise<T>} API response data
 *
 */
async function makeApiCall<T>(
  endpoint: string,
  parameters: WithingsRequestParams,
  accessToken: string,
): Promise<T> {
  try {
    const url = `${API_BASE_URL}${endpoint}`; // No version for measure endpoint

    // Filter out undefined parameters
    const cleanParameters = Object.fromEntries(
      Object.entries(parameters).filter(([, value]) => value !== undefined),
    );

    // For measure endpoint, use POST with form data
    const response = await fetch(url, {
      body: new URLSearchParams(cleanParameters as Record<string, string>).toString(),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      method: "POST",
    });

    if (!response.ok) {
      await response.text(); // NOTE: Consume response body
      throw new Error(
        `API call failed with status: ${String(response.status)} ${response.statusText}`,
      );
    }

    const responseData = (await response.json()) as unknown as T;
    return responseData;
  } catch (error) {
    // NOTE: Re-throw with context for better error handling
    throw new Error(
      `Withings API call failed: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Apply optional measurement properties to result object
 *
 * @param result - Target result object
 *
 * @param measurements - Source measurements object
 *
 */
function applyOptionalMeasurements(
  result: WithingsScaleData,
  measurements: Record<string, number | undefined>,
): void {
  const optionalFields: (keyof WithingsScaleData)[] = [
    "atrialFibrillation",
    "atrialFibrillationPPG",
    "basalMetabolicRate",
    "bmi",
    "bodyFat",
    "bodyTemperature",
    "bodyWater",
    "boneMass",
    "diastolicBP",
    "electrochemicalSkinConductance",
    "electrodermalActivity",
    "extracellularWater",
    "fatFreeMass",
    "fatMass",
    "fatMassSegments",
    "heartRate",
    "height",
    "intracellularWater",
    "muscleMass",
    "muscleMassSegments",
    "nerveHealthScore",
    "prInterval",
    "pulseWaveVelocity",
    "qrsInterval",
    "qtCorrected",
    "qtInterval",
    "skinTemperature",
    "spO2",
    "systolicBP",
    "temperature",
    "vascularAge",
    "visceralFat",
    "vo2Max",
  ];

  for (const field of optionalFields) {
    const value = measurements[field];
    if (value !== undefined) {
      result[field] = value as never;
    }
  }
}

/**
 * Transform Withings API measurements response to domain model
 *
 * @param response
 *
 * @param userId
 *
 * @returns {WithingsScaleData[]} Transformed scale data array
 *
 */
function transformMeasurementsResponse(
  response: WithingsMeasurementsResponse,
  userId: string,
): WithingsScaleData[] {
  // Reason: response.body.measuregrps is always defined per WithingsMeasurementsResponse interface
  if (response.body.measuregrps.length === 0) {
    return [];
  }

  return response.body.measuregrps.map((group) => {
    const measurements = transformApiMeasurements(group.measures);

    const result: WithingsScaleData = {
      id: group.grpid.toString(),
      timestamp: new Date(group.date * 1000),
      userId: userId,
      weight: measurements.weight ?? 0,
    };

    if (group.deviceid) {
      result.deviceId = group.deviceid;
    }

    applyOptionalMeasurements(result, measurements);

    return result;
  });
}

/**
 * Transform API measurements to domain measurements
 *
 * @param apiMeasurements
 *
 * @returns {Record<string, number | undefined>} Transformed measurements object
 *
 */
type MeasurementHandler = (value: number, measurements: Record<string, number | undefined>) => void;

/**
 * Measurement handlers mapping
 */
const MEASUREMENT_HANDLERS: Record<number, MeasurementHandler> = {
  [WITHINGS_MEASUREMENT_TYPES.ATRIAL_FIBRILLATION]: (value, measurements) => {
    measurements.atrialFibrillation = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.ATRIAL_FIBRILLATION_PPG]: (value, measurements) => {
    measurements.atrialFibrillationPPG = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.BASAL_METABOLIC_RATE]: (value, measurements) => {
    measurements.basalMetabolicRate = Math.round(value);
  },
  [WITHINGS_MEASUREMENT_TYPES.BODY_TEMPERATURE]: (value, measurements) => {
    measurements.bodyTemperature = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.BONE_MASS]: (value, measurements) => {
    measurements.boneMass = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.DIASTOLIC_BP]: (value, measurements) => {
    measurements.diastolicBP = Math.round(value);
  },
  [WITHINGS_MEASUREMENT_TYPES.ELECTROCHEMICAL_SKIN_CONDUCTANCE]: (value, measurements) => {
    measurements.electrochemicalSkinConductance = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.ELECTRODERMAL_ACTIVITY]: (value, measurements) => {
    measurements.electrodermalActivity = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.EXTRACELLULAR_WATER]: (value, measurements) => {
    measurements.extracellularWater = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.FAT_FREE_MASS]: (value, measurements) => {
    measurements.fatFreeMass = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.FAT_MASS]: (value, measurements) => {
    measurements.fatMass = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.FAT_MASS_SEGMENTS]: (value, measurements) => {
    measurements.fatMassSegments = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.FAT_RATIO]: (value, measurements) => {
    measurements.bodyFat = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.HEART_PULSE]: (value, measurements) => {
    measurements.heartRate = Math.round(value);
  },
  [WITHINGS_MEASUREMENT_TYPES.HEIGHT]: (value, measurements) => {
    measurements.height = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.HYDRATION]: (value, measurements) => {
    measurements.bodyWater = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.INTRACELLULAR_WATER]: (value, measurements) => {
    measurements.intracellularWater = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.MUSCLE_MASS]: (value, measurements) => {
    measurements.muscleMass = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.MUSCLE_MASS_SEGMENTS]: (value, measurements) => {
    measurements.muscleMassSegments = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.NERVE_HEALTH_SCORE]: (value, measurements) => {
    measurements.nerveHealthScore = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.PR_INTERVAL]: (value, measurements) => {
    measurements.prInterval = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.PULSE_WAVE_VELOCITY]: (value, measurements) => {
    measurements.pulseWaveVelocity = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.QRS_INTERVAL]: (value, measurements) => {
    measurements.qrsInterval = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.QT_CORRECTED]: (value, measurements) => {
    measurements.qtCorrected = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.QT_INTERVAL]: (value, measurements) => {
    measurements.qtInterval = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.SKIN_TEMPERATURE]: (value, measurements) => {
    measurements.skinTemperature = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.SPO2]: (value, measurements) => {
    measurements.spO2 = Math.round(value);
  },
  [WITHINGS_MEASUREMENT_TYPES.SYSTOLIC_BP]: (value, measurements) => {
    measurements.systolicBP = Math.round(value);
  },
  [WITHINGS_MEASUREMENT_TYPES.TEMPERATURE]: (value, measurements) => {
    measurements.temperature = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.VASCULAR_AGE]: (value, measurements) => {
    measurements.vascularAge = Math.round(value);
  },
  [WITHINGS_MEASUREMENT_TYPES.VISCERAL_FAT]: (value, measurements) => {
    measurements.visceralFat = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.VO2_MAX]: (value, measurements) => {
    measurements.vo2Max = value;
  },
  [WITHINGS_MEASUREMENT_TYPES.WEIGHT]: (value, measurements) => {
    measurements.weight = value;
  },
};

/**
 * Transform API measurements to domain measurements
 *
 * @param apiMeasurements
 *
 * @returns {Record<string, number | undefined>} Transformed measurements object
 *
 */
function transformApiMeasurements(
  apiMeasurements: WithingsApiMeasurement[],
): Record<string, number | undefined> {
  const measurements: Record<string, number | undefined> = {};

  for (const measurement of apiMeasurements) {
    // Withings uses negative units, so we need to divide by 10^|unit|
    const value = measurement.value / Math.pow(10, Math.abs(measurement.unit));

    const handler = MEASUREMENT_HANDLERS[measurement.type];
    if (handler) {
      handler(value, measurements);
    }
  }

  return measurements;
}

/**
 * Transform API device response to domain model
 *
 * @param apiDevice
 *
 * @returns {WithingsDevice} Transformed device object
 *
 */
function transformDeviceResponse(apiDevice: WithingsApiDevice): WithingsDevice {
  return {
    ...(apiDevice.battery !== undefined && { battery: apiDevice.battery }),
    ...(apiDevice.firmware !== undefined && { firmware: apiDevice.firmware }),
    id: apiDevice.id,
    ...(apiDevice.last_sync_time && {
      lastSyncTime: new Date(apiDevice.last_sync_time * 1000),
    }),
    model: apiDevice.model,
    type: apiDevice.type,
  };
}

/**
 * Extract number value from typed measurement
 *
 * @param value
 *
 * @returns {number | undefined} Number value or undefined
 *
 */
function extractNumber(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

/**
 * Extract string value from typed measurement
 *
 * @param value
 *
 * @returns {string} String value or empty string
 *
 */
function extractString(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

/**
 * Extract timestamp from database measurement
 *
 * @param databaseMeasurement
 *
 * @returns {string} ISO timestamp string
 *
 */
function extractTimestamp(databaseMeasurement: WithingsMeasurement): string {
  if (typeof databaseMeasurement === "object" && "measurement_timestamp" in databaseMeasurement) {
    const timestamp = databaseMeasurement.measurement_timestamp;
    return typeof timestamp === "string" ? timestamp : String(timestamp);
  }
  return new Date().toISOString();
}

/**
 * Transform database measurement to domain model
 *
 * @param databaseMeasurement
 *
 * @returns {WithingsScaleData} Transformed scale data object
 *
 */
function transformDatabaseMeasurement(databaseMeasurement: WithingsMeasurement): WithingsScaleData {
  const typedMeasurement = databaseMeasurement as {
    id?: unknown;
    measurement_timestamp?: unknown;
    weight_kg?: unknown;
    body_fat_percent?: unknown;
    muscle_mass_kg?: unknown;
    bone_mass_kg?: unknown;
    body_water_kg?: unknown;
    visceral_fat?: unknown;
    bmi?: unknown;
    heart_rate?: unknown;
    withings_user_id?: unknown;
    device_id?: unknown;
    fat_free_mass_kg?: unknown;
    fat_mass_kg?: unknown;
    fat_free_mass_percent?: unknown;
  };

  const result: WithingsScaleData = {
    id: extractString(typedMeasurement.id),
    timestamp: new Date(extractTimestamp(databaseMeasurement)),
    userId: extractString(typedMeasurement.withings_user_id),
    weight: extractNumber(typedMeasurement.weight_kg) ?? 0,
  };

  const bmi = extractNumber(typedMeasurement.bmi);
  if (bmi !== undefined) {
    result.bmi = bmi;
  }
  const bodyFat = extractNumber(typedMeasurement.body_fat_percent);
  if (bodyFat !== undefined) {
    result.bodyFat = bodyFat;
  }
  const bodyWater = extractNumber(typedMeasurement.body_water_kg);
  if (bodyWater !== undefined) {
    result.bodyWater = bodyWater;
  }
  const boneMass = extractNumber(typedMeasurement.bone_mass_kg);
  if (boneMass !== undefined) {
    result.boneMass = boneMass;
  }
  const deviceId = extractString(typedMeasurement.device_id);
  if (deviceId) {
    result.deviceId = deviceId;
  }
  const fatFreeMass = extractNumber(typedMeasurement.fat_free_mass_kg);
  if (fatFreeMass !== undefined) {
    result.fatFreeMass = fatFreeMass;
  }
  const fatFreeMassPercent = extractNumber(typedMeasurement.fat_free_mass_percent);
  if (fatFreeMassPercent !== undefined) {
    result.fatFreeMassPercent = fatFreeMassPercent;
  }
  const fatMass = extractNumber(typedMeasurement.fat_mass_kg);
  if (fatMass !== undefined) {
    result.fatMass = fatMass;
  }
  const heartRate = extractNumber(typedMeasurement.heart_rate);
  if (heartRate !== undefined) {
    result.heartRate = heartRate;
  }
  const muscleMass = extractNumber(typedMeasurement.muscle_mass_kg);
  if (muscleMass !== undefined) {
    result.muscleMass = muscleMass;
  }
  const visceralFat = extractNumber(typedMeasurement.visceral_fat);
  if (visceralFat !== undefined) {
    result.visceralFat = visceralFat;
  }

  return result;
}

/**
 * Withings Service Object
 * Provides a unified interface for all Withings API operations
 */
export const WithingsService = {
  getDevices,
  getMeasurements,
  getMeasurementsFromDatabase,
  getUserProfile,
  initialize,
  syncMeasurementsToDatabase,
  syncUserProfileToDatabase,
};
